import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Button, Chip, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, FormControl, InputLabel, LinearProgress, CircularProgress, Avatar, Grid, Divider, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonIcon from '@mui/icons-material/Person';
import { getLogoUrl } from '../../utils/logoUtils';
import { websocketService } from '../../services/emergencyWebsocket';
import { logoutUser, api, initFeatherIcons } from '../../utils/api';
import DriverTrackingMap from '../../components/maps/DriverTrackingMap';
import muiTheme from '../../theme/muiTheme';
import '../../styles/EmergencyDashboard.css';
import '../../styles/maps.css';
import '../dashboards/PatientDashboard.css';
import './DriverDashboard.css';

const REQUEST_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  STARTED_JOURNEY: 'started_journey',
  ON_THE_WAY: 'on_the_way',
  ALMOST_THERE: 'almost_there',
  LOOKING_FOR_PATIENT: 'looking_for_patient',
  RECEIVED_PATIENT: 'received_patient',
  DROPPING_OFF: 'dropping_off',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

const STATUS_LABELS = {
  [REQUEST_STATUS.PENDING]: 'Pending',
  [REQUEST_STATUS.ACCEPTED]: 'Accepted',
  [REQUEST_STATUS.STARTED_JOURNEY]: 'Started Journey',
  [REQUEST_STATUS.ON_THE_WAY]: 'On the Way',
  [REQUEST_STATUS.ALMOST_THERE]: 'Almost There',
  [REQUEST_STATUS.LOOKING_FOR_PATIENT]: 'Looking for Patient',
  [REQUEST_STATUS.RECEIVED_PATIENT]: 'Patient Received',
  [REQUEST_STATUS.DROPPING_OFF]: 'Dropping Off',
  [REQUEST_STATUS.COMPLETED]: 'Completed',
  [REQUEST_STATUS.CANCELLED]: 'Cancelled'
};

const STATUS_FLOW = [
  { status: REQUEST_STATUS.STARTED_JOURNEY, label: 'Start Journey' },
  { status: REQUEST_STATUS.ON_THE_WAY, label: 'On the Way' },
  { status: REQUEST_STATUS.ALMOST_THERE, label: 'Almost There' },
  { status: REQUEST_STATUS.LOOKING_FOR_PATIENT, label: 'Looking for Patient' },
  { status: REQUEST_STATUS.RECEIVED_PATIENT, label: 'Patient Received' },
  { status: REQUEST_STATUS.DROPPING_OFF, label: 'Dropping Off' },
  { status: REQUEST_STATUS.COMPLETED, label: 'Complete Journey' }
];

const STATUS_DESCRIPTIONS = {
  [REQUEST_STATUS.PENDING]: 'Waiting for driver to accept the request',
  [REQUEST_STATUS.ACCEPTED]: 'You have accepted the request. Please start your journey when ready.',
  [REQUEST_STATUS.STARTED_JOURNEY]: 'You have started your journey to the patient',
  [REQUEST_STATUS.ON_THE_WAY]: 'You are on your way to the patient',
  [REQUEST_STATUS.ALMOST_THERE]: 'You are approaching the patient\'s location',
  [REQUEST_STATUS.LOOKING_FOR_PATIENT]: 'You have arrived at the location and are looking for the patient',
  [REQUEST_STATUS.RECEIVED_PATIENT]: 'You have picked up the patient',
  [REQUEST_STATUS.DROPPING_OFF]: 'You are taking the patient to their destination',
  [REQUEST_STATUS.COMPLETED]: 'The journey has been completed',
  [REQUEST_STATUS.CANCELLED]: 'The request has been cancelled'
};

// Helper component for detail rows
const DetailRow = ({ label, value, isDarkMode }) => (
  <Box sx={{ 
    p: 1.5, 
    borderRadius: 1,
    bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.5)',
    border: isDarkMode 
      ? '1px solid rgba(59, 130, 246, 0.1)' 
      : '1px solid rgba(30, 64, 175, 0.05)'
  }}>
    <Typography 
      variant="caption" 
      sx={{ 
        fontWeight: 600, 
        textTransform: 'uppercase', 
        color: isDarkMode ? '#94A3B8' : '#64748B',
        fontSize: '0.7rem',
        letterSpacing: '0.5px'
      }}
    >
      {label}
    </Typography>
    <Typography 
      variant="body2" 
      sx={{ 
        fontWeight: 500,
        color: isDarkMode ? '#E0F2FE' : '#1E293B',
        mt: 0.5
      }}
    >
      {value}
    </Typography>
  </Box>
);

// Helper function for styled TextField
const StyledTextField = ({ isDarkMode, ...props }) => (
  <TextField
    {...props}
    variant="outlined"
    sx={{
      '& .MuiOutlinedInput-root': {
        bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
        color: isDarkMode ? '#E0F2FE' : '#1E293B',
        '& fieldset': {
          borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
        },
        '&:hover fieldset': {
          borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
        },
        '&.Mui-focused fieldset': {
          borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
        }
      },
      '& .MuiInputLabel-root': {
        color: isDarkMode ? '#94A3B8' : '#64748B',
        '&.Mui-focused': {
          color: isDarkMode ? '#60A5FA' : '#1E40AF',
        }
      },
      '& .MuiInputBase-input::placeholder': {
        color: isDarkMode ? '#64748B' : '#94A3B8',
        opacity: 1
      }
    }}
  />
);

// Helper function for Select styles
const getSelectStyles = (isDarkMode) => ({
  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.5)' : '#FFFFFF',
  color: isDarkMode ? '#E0F2FE' : '#1E293B',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: isDarkMode ? 'rgba(59, 130, 246, 0.5)' : 'rgba(30, 64, 175, 0.4)',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: isDarkMode ? '#60A5FA' : '#1E40AF',
  },
  '& .MuiSvgIcon-root': {
    color: isDarkMode ? '#60A5FA' : '#1E40AF',
  }
});

const EmergencyDriverDashboard = () => {
  const navigate = useNavigate();
  const outletContext = useOutletContext();
  const [isOnline, setIsOnline] = useState(true);
  const [requests, setRequests] = useState([]);
  const [currentRequest, setCurrentRequest] = useState(null);
  const [user, setUser] = useState(outletContext?.user || null);
  const [showStatusGuide, setShowStatusGuide] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [driverLocation, setDriverLocation] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [isDarkMode, setIsDarkMode] = useState(() => {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  const [logoUrl, setLogoUrl] = useState(getLogoUrl());
  
  // Monitor theme changes
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
      setIsDarkMode(isDark);
      setLogoUrl(getLogoUrl());
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });
    return () => observer.disconnect();
  }, []);
  
  // Toggle dark mode function
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.setAttribute('data-theme', !isDarkMode ? 'dark' : 'light');
    setLogoUrl(getLogoUrl());
  };
  
  // Profile state - will be redesigned
  const [profileData, setProfileData] = useState(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  // Removed isEditingProfile state - using activeTab === 'edit-profile' instead
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    email: '',
    licenseNumber: '',
    licensePicture: '',
    vehicleNumber: '',
    carModel: '',
    ambulanceType: '',
    carChassisNumber: '',
    carFitnessNumber: '',
    lastFitnessRenewalDate: '',
    lastTaxRenewalDate: '',
    providingOrganization: ''
  });
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  
  // New state for ride history
  const [rideHistory, setRideHistory] = useState([]);
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // New state for ride completion modal
  const [showRideCompletion, setShowRideCompletion] = useState(false);
  const [completedRide, setCompletedRide] = useState(null);

  // New function to fetch ride history
  const fetchRideHistory = useCallback(async () => {
    try {
      setIsLoadingHistory(true);
      
      // Get user ID from user object (try both _id and id)
      const userId = user?._id || user?.id;
      if (!userId) {
        console.warn('User ID not found, cannot fetch ride history');
        setRideHistory([]);
        setTotalEarnings(0);
        return;
      }
      
      // Call the backend API
      console.log('=== Fetching ride history ===');
      console.log('Driver userId:', userId);
      console.log('User object:', { _id: user?._id, id: user?.id });
      const response = await api.get(`/emergency/ride-history?userType=driver&userId=${userId}`);
      
      console.log('Ride history API response:', response.data);
      console.log('Number of rides returned:', response.data?.data?.rides?.length || 0);
      
      if (response.data.success) {
        console.log('Raw rides from API:', response.data.data.rides);
        
        // Transform the API response to match the frontend format
        const transformedRides = response.data.data.rides.map(ride => {
          const fare = ride.rideDetails?.fare?.totalFare || ride.payment?.amount || 0;
          const ambulanceType = ride.driverInfo?.ambulanceType || 
                               ride.driverInfo?.vehicleType || 
                               ride.rideDetails?.ambulanceType || 
                               'Basic';
          
          return {
          id: ride.requestId,
            requestId: ride.requestId,
          patientName: ride.patientInfo?.name || 'Unknown Patient',
            pickupLocation: ride.location || ride.rideDetails?.pickup?.address || ride.initialRequestedLocation?.address || 'Unknown Location',
          dropoffLocation: ride.rideDetails?.destination?.address || ride.rideDetails?.destination || 'Unknown Location',
          date: new Date(ride.createdAt).toLocaleDateString(),
          time: new Date(ride.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          duration: ride.rideDetails?.metrics?.totalDuration ? `${ride.rideDetails.metrics.totalDuration} mins` : 'N/A',
          distance: ride.rideDetails?.metrics?.totalDistance ? `${ride.rideDetails.metrics.totalDistance} km` : 'N/A',
            fare: fare,
            ambulanceType: ambulanceType,
            status: ride.status,
            completedAt: ride.statusHistory?.completed || ride.createdAt
          };
        });
        
        console.log('Transformed rides:', transformedRides);
        setRideHistory(transformedRides);
        
        // Calculate total earnings from completed rides only
        const total = transformedRides
          .filter(ride => ride.status === 'completed')
          .reduce((sum, ride) => sum + (ride.fare || 0), 0);
        setTotalEarnings(total);
        console.log('Total earnings calculated:', total);
      } else {
        console.error('Failed to fetch ride history:', response.data.message);
        setRideHistory([]);
        setTotalEarnings(0);
      }
      
    } catch (error) {
      console.error('Error fetching ride history:', error);
      // Set empty arrays on error
      setRideHistory([]);
      setTotalEarnings(0);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user?._id, user?.id]);

  // Handle edit profile - No longer needed as tab handles it
  // Removed handleEditProfile and handleCancelEdit functions

  // Handle save profile
  const handleSaveProfile = async () => {
    try {
      setIsSavingProfile(true);
      const response = await api.put('/emergency/driver/profile', {
        name: editFormData.name,
        phone: editFormData.phone,
        email: editFormData.email,
        licenseNumber: editFormData.licenseNumber,
        vehicleNumber: editFormData.vehicleNumber,
        carRegistration: editFormData.vehicleNumber,
        ambulanceType: editFormData.ambulanceType,
        chassisNumber: editFormData.carChassisNumber,
        carChassisNumber: editFormData.carChassisNumber,
        carModel: editFormData.carModel,
        carFitnessNumber: editFormData.carFitnessNumber,
        lastFitnessRenewalDate: editFormData.lastFitnessRenewalDate,
        lastTaxRenewalDate: editFormData.lastTaxRenewalDate,
        providingOrganization: editFormData.providingOrganization,
        vehicleType: editFormData.ambulanceType?.toLowerCase() || 'standard'
      });

      if (response.data.success) {
        // Refresh profile data
        await fetchDriverProfile();
        // Don't change tab, stay on edit-profile tab
        alert('Profile updated successfully!');
      } else {
        alert('Failed to update profile: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Error updating profile: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSavingProfile(false);
    }
  };

  // Fetch driver profile data
  const fetchDriverProfile = useCallback(async () => {
      // #region agent log
    fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:fetchDriverProfile-start',message:'Starting to fetch driver profile',data:{hasUser:!!user,userId:user?._id},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF1'})}).catch(()=>{});
    // #endregion
    setIsLoadingProfile(true);
    try {
      const response = await api.get('/emergency/driver/profile');
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:fetchDriverProfile-response',message:'Profile API response received',data:{success:response.data.success,hasData:!!response.data.data,dataKeys:response.data.data ? Object.keys(response.data.data) : []},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF2'})}).catch(()=>{});
      // #endregion
      if (response.data.success) {
        setProfileData(response.data.data);
      } else {
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:fetchDriverProfile-no-success',message:'API returned success=false',data:{message:response.data.message},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF3'})}).catch(()=>{});
        // #endregion
        setProfileData({}); // Set empty object so we can still render with user data
      }
    } catch (error) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:fetchDriverProfile-error',message:'Error fetching profile',data:{errorMessage:error.message,errorStatus:error.response?.status,errorData:error.response?.data},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF4'})}).catch(()=>{});
      // #endregion
      console.error('Error fetching driver profile:', error);
      setProfileData({}); // Set empty object so we can still render with user data
    } finally {
      setIsLoadingProfile(false);
    }
  }, [user]);

  useEffect(() => {
    // Initialize feather icons
    // Initialize Feather icons after a small delay to ensure DOM is ready
    setTimeout(() => {
      try {
        if (typeof window !== 'undefined' && window.feather) {
          window.feather.replace();
        } else if (typeof initFeatherIcons === 'function') {
          initFeatherIcons();
        }
      } catch (error) {
        console.warn('Error initializing Feather icons:', error);
      }
    }, 100);
    const timer = setTimeout(() => initFeatherIcons(), 300);
    return () => clearTimeout(timer);
  }, [activeTab, sidebarCollapsed, mobileMenuOpen]);

  useEffect(() => {
    // Get user data from localStorage
    const userData = JSON.parse(localStorage.getItem('user') || '{}');
    if (outletContext?.user) {
      setUser(outletContext.user);
    } else {
      setUser(userData);
    }

    // Fetch ride history on component mount
    fetchRideHistory();
  }, []);

  // Fetch profile when profile tab is accessed
  useEffect(() => {
    if (activeTab === 'profile' && !profileData && !isLoadingProfile) {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:useEffect-profile-tab',message:'Profile tab activated, fetching profile',data:{activeTab,hasProfileData:!!profileData,isLoadingProfile},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF6'})}).catch(()=>{});
      // #endregion
      fetchDriverProfile();
    }
  }, [activeTab, fetchDriverProfile, profileData, isLoadingProfile]);

    // Connect to WebSocket and authenticate as driver
  useEffect(() => {
    console.log('Connecting to emergency WebSocket from Driver Dashboard...');
    console.log('User data:', user);
    
    websocketService.connect();
    
    // Explicitly authenticate as driver with retry logic
    const authenticateDriver = () => {
      if (websocketService.socket?.connected && user) {
        const userId = user._id || user.id;
        console.log('Authenticating driver with ID:', userId);
        websocketService.socket.emit('authenticate', { 
          userId: userId, 
          userType: 'driver' 
        });
        
        // Listen for authentication confirmation
        websocketService.socket.once('authenticated', (data) => {
          console.log('Driver authenticated successfully:', data);
          console.log('Driver should now be in drivers room and receiving broadcasts');
        });
      } else if (!websocketService.socket?.connected) {
        console.log('Socket not connected yet, retrying authentication in 1 second...');
        setTimeout(authenticateDriver, 1000);
      } else {
        console.log('No user data available for authentication');
      }
    };
    
    // Start authentication after a short delay to ensure connection
    setTimeout(authenticateDriver, 500);

    // Subscribe to new requests
    const newRequestSubscription = websocketService.subscribe('new_request', async (data) => {
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:468',message:'WebSocket new_request received',data:{requestId:data.id,hasLocation:!!data.location,locationType:typeof data.location,patientName:data.patientInfo?.name,emergencyType:data.emergencyType,isOnline},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'G'})}).catch(()=>{});
      // #endregion agent log
      console.log('✅ Received new request in driver dashboard:', data);
      console.log('New request location data:', data.location);
      
      // Prepare request data
      let requestData = { ...data };
      
      // Try to fetch initial location from database
      try {
        const response = await api.get(`/emergency/details/${data.id}`);
        if (response.data.success && response.data.data?.initialRequestedLocation) {
          const initialLocation = response.data.data.initialRequestedLocation;
          console.log('Fetched initial location from database:', initialLocation);
          // Use initial location from database
          requestData.initialRequestedLocation = initialLocation;
          if (initialLocation.latitude && initialLocation.longitude) {
            // Set as both location and patientLocation for map display
            requestData.location = {
              latitude: initialLocation.latitude,
              longitude: initialLocation.longitude,
              accuracy: initialLocation.accuracy,
              address: initialLocation.address || data.location
            };
            requestData.patientLocation = {
              latitude: initialLocation.latitude,
              longitude: initialLocation.longitude,
              accuracy: initialLocation.accuracy,
              address: initialLocation.address || data.location,
              timestamp: initialLocation.timestamp
            };
            console.log('✅ Set patient location from initial requested location:', requestData.patientLocation);
          }
        }
      } catch (dbError) {
        console.warn('Could not fetch initial location from database, using WebSocket data:', dbError);
      // Normalize location data to ensure consistent property names
        if (requestData.location && typeof requestData.location === 'object') {
          requestData.location = {
            latitude: requestData.location.latitude || requestData.location.lat,
            longitude: requestData.location.longitude || requestData.location.lng,
            accuracy: requestData.location.accuracy,
            address: requestData.location.address || requestData.location
          };
          console.log('Normalized location from WebSocket:', requestData.location);
        }
      }
      
      setRequests(prev => {
        // Check if request already exists
        if (prev.some(req => req.id === data.id)) {
          // #region agent log
          fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:485',message:'Request already exists, skipping',data:{requestId:data.id,prevRequestsCount:prev.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'H'})}).catch(()=>{});
          // #endregion agent log
          console.log('Request already exists, skipping:', data.id);
          return prev;
        }
        // #region agent log
        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:489',message:'Adding new request to state',data:{requestId:data.id,prevRequestsCount:prev.length,newRequestsCount:prev.length+1},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
        // #endregion agent log
        console.log('Adding new request to state:', requestData);
        return [...prev, { ...requestData, status: 'pending' }];
      });
    });

    // Subscribe to request status updates
    const statusUpdateSubscription = websocketService.subscribe('request_status_update', (data) => {
      console.log('Received status update in driver dashboard:', data);
      setRequests(prev => 
        prev.map(req => 
          req.id === data.requestId ? { ...req, status: data.status } : req
        )
      );
      if (currentRequest?.id === data.requestId) {
        setCurrentRequest(prev => ({ ...prev, status: data.status }));
      }
    });
    
    // Subscribe to request cancellation notifications
    const cancelRequestSubscription = websocketService.subscribe('request_cancelled', (data) => {
      console.log('Request cancelled by patient:', data);
      const { requestId, message } = data;
      
      // Show notification to driver
      alert(`Request ${requestId} has been cancelled by the patient. ${message || ''}`);
      
      // Remove from requests list
      setRequests(prev => prev.filter(req => req.id !== requestId));
      
      // Clear current request if it's the cancelled one
      if (currentRequest?.id === requestId) {
        setCurrentRequest(null);
        // Update driver status to available
        updateDriverStatus('available');
      }
    });

    // Subscribe to location updates from patient
    const locationReceivedSubscription = websocketService.subscribe('location:received', (data) => {
      console.log('📍 Received location update in driver dashboard:', data);
      if (currentRequest?.id === data.requestId && data.userType === 'patient') {
        const lat = data.location?.latitude || data.location?.lat;
        const lng = data.location?.longitude || data.location?.lng;
        
        // Only update if we have valid coordinates
        if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
          const locationData = {
            latitude: lat,
            longitude: lng,
            accuracy: data.location.accuracy,
            timestamp: data.timestamp,
            address: data.location.address
          };
          
          console.log('✅ Valid patient location received:', locationData);
          
          // Update the current request with patient location
          setCurrentRequest(prev => ({
            ...prev,
            location: locationData,
            patientLocation: locationData
          }));
        } else {
          console.log('❌ Invalid patient location coordinates:', { lat, lng, rawLocation: data.location });
        }
      }
    });

    // Subscribe to location permission events
    const locationPermissionSubscription = websocketService.subscribe('location:permission-granted', (data) => {
      console.log('Location permission granted:', data);
      if (currentRequest?.id === data.requestId) {
        console.log('Location sharing is now available for this request');
      }
    });

    // Subscribe to tracking active events
    const trackingActiveSubscription = websocketService.subscribe('location:tracking-active', (data) => {
      console.log('Location tracking is now active:', data);
      if (currentRequest?.id === data.requestId) {
        console.log('Real-time tracking is active between driver and patient');
      }
    });

    // Subscribe to payment completed events - refresh ride history when payment is done
    const paymentCompletedSubscription = websocketService.subscribe('payment_completed', (data) => {
      console.log('Payment completed notification received:', data);
      // Refresh ride history to show the newly completed ride
      if (data.requestId) {
        console.log('Refreshing ride history after payment completion for request:', data.requestId);
        fetchRideHistory();
      }
    });

    // Cleanup function
    return () => {
      console.log('Cleaning up WebSocket subscriptions');
      websocketService.unsubscribe('new_request', newRequestSubscription);
      websocketService.unsubscribe('request_status_update', statusUpdateSubscription);
      websocketService.unsubscribe('location:received', locationReceivedSubscription);
      websocketService.unsubscribe('location:permission-granted', locationPermissionSubscription);
      websocketService.unsubscribe('location:tracking-active', trackingActiveSubscription);
      websocketService.unsubscribe('payment_completed', paymentCompletedSubscription);
      websocketService.unsubscribe('request_cancelled', cancelRequestSubscription);
      // No need to remove connect listener for WebSocket
    };
  }, [currentRequest?.id, fetchRideHistory]);

  const handleAcceptRequest = async (request) => {
    setIsLoading(true);
    try {
      console.log('Accepting request:', request);
      
      // Get patient phone number from request data
      const patientPhone = request?.patientInfo?.phone || request?.patientInfo?.patientContact || 'N/A';
      console.log('Patient phone number from request:', patientPhone);
      
      // Get driver info with proper null checks
      const driverInfo = {
        id: user?._id || user?.id || 'test-driver-id',
        name: user?.name || 'Test Driver',
        phone: profileData?.mobileNumber || profileData?.phone || user?.phone || user?.driverDetails?.phone || '1234567890',
        vehicleType: profileData?.ambulanceType || user?.driverDetails?.ambulanceType || user?.driverDetails?.vehicleType || 'standard',
        vehicleNumber: profileData?.carRegistrationNumber || profileData?.vehicleNumber || user?.driverDetails?.vehicleNumber || user?.driverDetails?.carRegistrationNumber || 'TEST-123',
        location: user?.location || 'Unknown'
      };
      
      console.log('Driver info prepared:', driverInfo);

      const acceptData = {
        requestId: request.id,
        driverId: driverInfo.id,
        driver: driverInfo,
        status: 'accepted',
        timestamp: new Date().toISOString()
      };

      console.log('Sending accept request with data:', acceptData);
      await websocketService.send('accept_request', acceptData);

      // Also send a request status update
      const statusUpdate = {
        requestId: request.id,
        status: 'accepted',
        driver: driverInfo,
        timestamp: new Date().toISOString()
      };
      console.log('Sending request status update:', statusUpdate);
      await websocketService.send('request_status_update', statusUpdate);

      // Fetch request details from database to get initial location and patient info
      try {
        const response = await api.get(`/emergency/details/${request.id}`);
        if (response.data.success && response.data.data) {
          const dbRequest = response.data.data;
          console.log('Fetched request from database:', dbRequest);
          
          // Use initial location from database if available
          const initialLocation = dbRequest.initialRequestedLocation;
          if (initialLocation && initialLocation.latitude && initialLocation.longitude) {
            console.log('Using initial requested location from database:', initialLocation);
            request.initialRequestedLocation = initialLocation;
            // Set as both location and patientLocation for map display
            request.location = {
              latitude: initialLocation.latitude,
              longitude: initialLocation.longitude,
              accuracy: initialLocation.accuracy,
              address: initialLocation.address
            };
            request.patientLocation = {
              latitude: initialLocation.latitude,
              longitude: initialLocation.longitude,
              accuracy: initialLocation.accuracy,
              address: initialLocation.address,
              timestamp: initialLocation.timestamp
            };
            console.log('✅ Set patient location from initial requested location for map:', request.patientLocation);
          }
          
          // Update patient info from database if available
          if (dbRequest.patientInfo) {
            request.patientInfo = {
              ...request.patientInfo,
              ...dbRequest.patientInfo
            };
            console.log('Updated patient info from database:', request.patientInfo);
          }
          
        }
      } catch (dbError) {
        console.warn('Could not fetch request from database, using WebSocket data:', dbError);
      }

      // Ensure patient phone is available in the request
      const finalPatientPhone = request?.patientInfo?.phone || patientPhone;
      console.log('Final patient phone number:', finalPatientPhone);
      
      setCurrentRequest({ 
        ...request, 
        status: 'accepted', 
        driverInfo,
        patientInfo: {
          ...request.patientInfo,
          phone: finalPatientPhone
        }
      });
      setRequests(prev => prev.filter(req => req.id !== request.id));

      // Refresh ride history immediately after accepting request
      console.log('Refreshing ride history after accepting request');
      fetchRideHistory();

      // Request location sharing permission after accepting
      setTimeout(() => {
        console.log('Requesting location permission for real-time tracking');
        websocketService.send('location:request-permission', { requestId: request.id });
      }, 1000);
    } catch (error) {
      console.error('Error accepting request:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectRequest = (request) => {
    console.log('Rejecting request:', request);
    try {
      websocketService.send('reject_request', {
        requestId: request.id,
        driverId: user?.id || 'test-driver-id'
      });
      setRequests(prev => prev.filter(req => req.id !== request.id));
    } catch (error) {
      console.error('Error rejecting request:', error);
    }
  };

  const updateRequestStatus = async (newStatus) => {
    if (!currentRequest) return;

    try {
      setIsLoading(true);
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:749',message:'updateRequestStatus called',data:{newStatus,hasCurrentRequest:!!currentRequest,currentRequestId:currentRequest?.id,hasProfileData:!!profileData,hasUser:!!user,profileDataKeys:profileData ? Object.keys(profileData) : [],userKeys:user ? Object.keys(user) : []},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
      // #endregion
      const statusUpdate = {
        requestId: currentRequest.id,
        status: newStatus,
        timestamp: new Date().toISOString(),
        driver: {
          id: user.id,
          name: user.name,
          phone: profileData?.mobileNumber || profileData?.phone || user?.phone || user?.driverDetails?.phone || 'N/A',
          vehicleType: profileData?.ambulanceType || user?.driverDetails?.ambulanceType || 'N/A',
          vehicleNumber: profileData?.carRegistrationNumber || user?.driverDetails?.carNumber || 'N/A',
          location: user.location,
          status: newStatus
        }
      };
      // #region agent log
      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:767',message:'Status update object created',data:{statusUpdate,driverPhone:statusUpdate.driver.phone,driverVehicleType:statusUpdate.driver.vehicleType,driverVehicleNumber:statusUpdate.driver.vehicleNumber},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'B'})}).catch(()=>{});
      // #endregion

      console.log('Sending status update:', statusUpdate);
      await websocketService.send('request_status_update', statusUpdate);

      // If starting journey, save to backend
      if (newStatus === REQUEST_STATUS.STARTED_JOURNEY) {
        const token = localStorage.getItem('token');
        if (token) {
          try {
            const response = await fetch(`/api/emergency/ride/${currentRequest.id}/start`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                pickupLocation: {
                  address: currentRequest.location,
                  latitude: currentRequest.location?.latitude || 0,
                  longitude: currentRequest.location?.longitude || 0
                },
                estimatedPickupTime: new Date(Date.now() + 10 * 60 * 1000).toISOString() // 10 minutes from now
              })
            });
            
            const data = await response.json();
            if (!data.success) {
              console.error('Failed to save ride start:', data.message);
            }
          } catch (error) {
            console.error('Error saving ride start:', error);
          }
        }
      }

      // Update local state
      setCurrentRequest(prev => {
        const updated = {
          ...prev,
          status: newStatus,
          lastUpdate: new Date().toISOString(),
          driverInfo: {
            ...prev.driverInfo,
            ...statusUpdate.driver
          }
        };
        console.log('Updated current request:', updated);
        return updated;
      });

      // Also update in requests list if it exists there
      setRequests(prev => 
        prev.map(req => 
          req.id === currentRequest.id 
            ? { ...req, status: newStatus }
            : req
        )
      );

      // Update driver status
      await updateDriverStatus(newStatus);
    } catch (error) {
      console.error('Error updating request status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to update driver status
  const updateDriverStatus = async (status) => {
    try {
      // Update driver status in WebSocket
      const driverStatusUpdate = {
        driverId: user.id,
        status: status,
        timestamp: new Date().toISOString(),
        location: driverLocation || user.location
      };
      
      await websocketService.send('driver_status_update', driverStatusUpdate);
      console.log('Driver status updated:', driverStatusUpdate);
    } catch (error) {
      console.error('Error updating driver status:', error);
    }
  };

  // Function to handle status updates (unused - keeping for future reference)
  // const handleStatusUpdate = async (status) => {
  //   if (!currentRequest) return;

  //   try {
  //     setIsLoading(true);
  //     const statusUpdate = {
  //       requestId: currentRequest.id,
  //       status: status,
  //       timestamp: new Date().toISOString(),
  //       driver: {
  //         id: user.id,
  //         name: user.name,
  //         phone: profileData.mobileNumber || user.phone,
  //         vehicleType: profileData.ambulanceType,
  //         vehicleNumber: profileData.carRegistrationNumber,
  //         location: user.location,
  //         status: status
  //       }
  //     };

  //     console.log('Sending status update:', statusUpdate);
  //     await websocketService.send('request_status_update', statusUpdate);

  //     // Update local state
  //     setCurrentRequest(prev => {
  //       const updated = {
  //         ...prev,
  //         status: status,
  //         lastUpdate: new Date().toISOString(),
  //         driverInfo: {
  //           ...prev.driverInfo,
  //           ...statusUpdate.driver
  //         }
  //       };
  //       console.log('Updated current request:', updated);
  //       return updated;
  //     });

  //     // Also update in requests list if it exists there
  //     setRequests(prev => 
  //       prev.map(req => 
  //         req.id === currentRequest.id 
  //           ? { ...req, status: status }
  //           : req
  //       )
  //     );

  //     // Update driver status
  //     await updateDriverStatus(status);
  //   } catch (error) {
  //     console.error('Error updating status:', error);
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  const getNextStatus = (currentStatus) => {
    const currentIndex = STATUS_FLOW.findIndex(step => step.status === currentStatus);
    return currentIndex < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIndex + 1] : null;
  };

  const getStatusProgress = (currentStatus) => {
    const currentIndex = STATUS_FLOW.findIndex(step => step.status === currentStatus);
    return ((currentIndex + 1) / STATUS_FLOW.length) * 100;
  };

  const toggleOnlineStatus = () => {
    setIsOnline(!isOnline);
  };



  // Function to refresh ride history
  const refreshRideHistory = () => {
    fetchRideHistory();
  };

  // Function to get ambulance type color
  const getAmbulanceTypeColor = (type) => {
    switch (type) {
      case 'vip':
        return 'text-yellow-800 bg-yellow-200';
      case 'critical':
        return 'text-red-800 bg-red-200';
      default:
        return 'text-blue-800 bg-blue-200';
    }
  };

  // Function to format currency (BDT)
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-BD', {
      style: 'currency',
      currency: 'BDT',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleSignOut = async () => {
    try {
      await logoutUser();
      websocketService.disconnect();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Fallback: clear local storage and navigate even if API call fails
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      websocketService.disconnect();
      navigate('/login');
    }
  };

  // Function to handle ride completion
  const handleRideCompletion = async () => {
    if (!currentRequest) return;

    try {
      setIsLoading(true);
      const statusUpdate = {
        requestId: currentRequest.id,
        status: REQUEST_STATUS.COMPLETED,
        timestamp: new Date().toISOString(),
        driver: {
          id: user.id,
          name: user.name,
          phone: profileData?.mobileNumber || profileData?.phone || user?.phone || user?.driverDetails?.phone || 'N/A',
          vehicleType: profileData?.ambulanceType || user?.driverDetails?.ambulanceType || 'N/A',
          vehicleNumber: profileData?.carRegistrationNumber || user?.driverDetails?.carNumber || 'N/A',
          location: user.location,
          status: REQUEST_STATUS.COMPLETED
        }
      };

      console.log('Sending ride completion update:', statusUpdate);
      await websocketService.send('request_status_update', statusUpdate);

      // Create completed ride record
      // Calculate ride metrics
      const rideDuration = calculateRideDuration(currentRequest.startTime);
      const rideDistance = calculateRideDistance(currentRequest.location, driverLocation || user.location);
      
      // Get ambulance type for fare calculation
      const ambulanceType = profileData?.ambulanceType || 
                           user?.driverDetails?.ambulanceType || 
                           currentRequest?.driverInfo?.ambulanceType || 
                           'Basic';
      console.log('Calculating fare for ambulance type:', ambulanceType);
      
      const rideFare = calculateRideFare(currentRequest.emergencyType, currentRequest.location, driverLocation || user.location);
      console.log('Calculated ride fare:', rideFare, 'BDT for', ambulanceType);
      
      // Save ride completion to backend via API
      try {
        console.log('Saving ride completion to backend API...');
        const ambulanceType = profileData?.ambulanceType || 
                             user?.driverDetails?.ambulanceType || 
                             currentRequest?.driverInfo?.ambulanceType || 
                             'Basic';
        
        const response = await api.post(`/emergency/ride/${currentRequest.id}/complete`, {
              dropoffLocation: {
            address: driverLocation?.address || driverLocation || user?.location || 'Unknown',
            latitude: driverLocation?.latitude || driverLocation?.lat || 0,
            longitude: driverLocation?.longitude || driverLocation?.lng || 0
          },
          totalDistance: parseFloat(rideDistance.replace(' km', '')) || 0,
          totalDuration: parseInt(rideDuration.replace(' mins', '')) || 0,
              averageSpeed: 35, // Mock average speed
          finalFare: rideFare || 0,
          ambulanceType: ambulanceType
        });
        
        console.log('Ride completion API response:', response.data);
        if (response.data.success) {
          console.log('✅ Ride completion saved successfully to database');
        } else {
          console.error('Failed to save ride completion:', response.data.message);
          }
        } catch (error) {
        console.error('Error saving ride completion to API:', error);
        // Also try WebSocket as fallback
        try {
          console.log('Trying WebSocket fallback for ride completion...');
          await websocketService.send('ride:complete', {
            requestId: currentRequest.id,
            dropoffLocation: {
              address: driverLocation?.address || driverLocation || user?.location || 'Unknown',
              latitude: driverLocation?.latitude || driverLocation?.lat || 0,
              longitude: driverLocation?.longitude || driverLocation?.lng || 0
            },
            totalDistance: parseFloat(rideDistance.replace(' km', '')) || 0,
            totalDuration: parseInt(rideDuration.replace(' mins', '')) || 0,
            averageSpeed: 35,
            finalFare: rideFare || 0
          });
          console.log('✅ Ride completion sent via WebSocket');
        } catch (wsError) {
          console.error('Error sending ride completion via WebSocket:', wsError);
        }
      }

      const newRide = {
        id: currentRequest.id,
        patientName: currentRequest.patientInfo?.name || 'Anonymous',
        pickupLocation: currentRequest.location,
        dropoffLocation: driverLocation || user.location,
        date: new Date().toISOString(),
        time: new Date().toLocaleTimeString(),
        duration: rideDuration,
        distance: rideDistance,
        fare: rideFare,
        ambulanceType: profileData?.ambulanceType || user?.driverDetails?.ambulanceType || 'N/A',
        status: 'completed',
        requestId: currentRequest.id
      };

      // Refresh ride history from backend to ensure consistency
      // Add a small delay to allow database to update
      setTimeout(async () => {
        console.log('Refreshing ride history after completion...');
      await fetchRideHistory();
      }, 1000);

      // Show completion modal with the completed ride info
      setCompletedRide(newRide);
      setShowRideCompletion(true);

      // Update local state
      setCurrentRequest(prev => {
        const updated = {
          ...prev,
          status: REQUEST_STATUS.COMPLETED,
          lastUpdate: new Date().toISOString(),
          driverInfo: {
            ...prev.driverInfo,
            ...statusUpdate.driver
          }
        };
        console.log('Updated current request to completed:', updated);
        return updated;
      });

      // Also update in requests list if it exists there
      setRequests(prev => 
        prev.map(req => 
          req.id === currentRequest.id 
            ? { ...req, status: REQUEST_STATUS.COMPLETED }
            : req
        )
      );

      // Update driver status to available
      await updateDriverStatus('available');

      // Clear current request
      setCurrentRequest(null);

    } catch (error) {
      console.error('Error completing ride:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to calculate ride duration
  const calculateRideDuration = (startTime) => {
    if (!startTime) return '0 mins';
    const start = new Date(startTime);
    const end = new Date();
    const diffMs = end - start;
    const diffMins = Math.round(diffMs / (1000 * 60));
    return `${diffMins} mins`;
  };

  // Function to calculate ride distance (mock)
  const calculateRideDistance = (start, end) => {
    // Mock distance calculation - replace with actual calculation
    return `${Math.floor(Math.random() * 20 + 5)} km`;
  };

  // Function to calculate ride fare based on ambulance type
  const calculateRideFare = (emergencyType, start, end) => {
    // Get ambulance type from profile or user data
    const ambulanceType = profileData?.ambulanceType || 
                         user?.driverDetails?.ambulanceType || 
                         currentRequest?.driverInfo?.ambulanceType || 
                         'Basic';
    
    console.log('Calculating fare for ambulance type:', ambulanceType);
    
    // Base fare based on ambulance type (as per requirements)
    let baseFare = 500; // Default Basic
    switch (ambulanceType) {
      case 'Basic':
        baseFare = 500;
        break;
      case 'Premium':
        baseFare = 2000;
        break;
      case 'ICU':
        baseFare = 5000;
        break;
      case 'NICU':
        baseFare = 6000;
        break;
      default:
        baseFare = 500; // Default to Basic
        break;
    }

    console.log('Base fare for', ambulanceType, ':', baseFare, 'BDT');
    
    // Distance multiplier (mock - can be enhanced with actual distance calculation)
    const distance = Math.floor(Math.random() * 20 + 5);
    const distanceMultiplier = 1 + (distance * 0.05); // 5% per km
    
    // Time multiplier (rush hour, night, etc.)
    const hour = new Date().getHours();
    let timeMultiplier = 1;
    if (hour >= 7 && hour <= 9) timeMultiplier = 1.15; // Morning rush
    if (hour >= 17 && hour <= 19) timeMultiplier = 1.15; // Evening rush
    if (hour >= 22 || hour <= 6) timeMultiplier = 1.2; // Night

    const totalFare = Math.round(baseFare * distanceMultiplier * timeMultiplier);
    console.log('Total calculated fare:', totalFare, 'BDT (base:', baseFare, ', distance:', distanceMultiplier, ', time:', timeMultiplier, ')');
    return totalFare;
  };

  // Function to close ride completion modal
  const closeRideCompletion = () => {
    setShowRideCompletion(false);
    setCompletedRide(null);
  };

  // Function to draw path between driver and patient (unused - keeping for future reference)
  // const drawPathToPatient = () => {
  //   if (!currentRequest || !user.location) return null;

  //   const driverLatLng = user.location;
  //   const patientLatLng = currentRequest.location;

  //   // Create path coordinates (simplified straight line for now)
  //   // In a real implementation, you'd use Google Maps Directions API or similar
  //   const pathCoordinates = [
  //     { lat: driverLatLng.lat, lng: driverLatLng.lng },
  //     { lat: patientLatLng.lat, lng: patientLatLng.lng }
  //   ];

  //   return pathCoordinates;
  // };

  // Function to get patient location marker (unused - keeping for future reference)
  // const getPatientLocationMarker = () => {
  //   if (!currentRequest) return null;

  //   return {
  //     position: currentRequest.location,
  //     title: `Patient: ${currentRequest.patientInfo?.name || 'Anonymous'}`,
  //     icon: {
  //       url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
  //         <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
  //           <circle cx="16" cy="16" r="16" fill="#EF4444"/>
  //           <circle cx="16" cy="16" r="8" fill="white"/>
  //           <circle cx="16" cy="16" r="4" fill="#EF4444"/>
  //         </svg>
  //       `),
  //       scaledSize: { width: 32, height: 32 },
  //       anchor: { x: 16, y: 16 }
  //     }
  //   };
  // };

  return (
    <ThemeProvider theme={muiTheme}>
    <div className={`driver-dashboard-new patient-dashboard-new`}>
      <div className="dashboard-layout">
        {/* Mobile Menu Toggle Button */}
        <button 
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle menu"
        >
          <i data-feather={mobileMenuOpen ? "x" : "menu"}></i>
        </button>

        {/* Mobile Overlay */}
        {mobileMenuOpen && (
          <div 
            className="mobile-overlay" 
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Modern Sidebar Navigation */}
        <aside className={`sidebar-new ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          {/* Sidebar Header */}
          <div className="sidebar-header-new">
            <div className="sidebar-logo-new">
              <div className="logo-container-new">
                <img 
                  src={logoUrl} 
                  alt="WeHeal" 
                  className="logo-image-new"
                  style={{ height: sidebarCollapsed ? '40px' : '48px', width: 'auto', objectFit: 'contain' }}
                />
                {!sidebarCollapsed && (
                  <div className="logo-text-container-new">
                    <span className="logo-text-new">WeHeal</span>
                    <span className="logo-tagline-new">Driver Dashboard</span>
                  </div>
                )}
              </div>
            </div>
            <button 
              className="sidebar-collapse-btn-new"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <i data-feather={sidebarCollapsed ? "chevron-right" : "chevron-left"}></i>
            </button>
          </div>

          {/* User Profile Card */}
          {!sidebarCollapsed && (
            <div className="sidebar-profile-card-new">
              <div className="profile-avatar-new">
                {user?.profilePicture ? (
                  <img src={user.profilePicture} alt={user?.name} />
                ) : (
                  <div className="profile-avatar-placeholder-new">
                    <i data-feather="user"></i>
                  </div>
                )}
                <div className={`profile-status-new ${isOnline ? 'online' : 'offline'}`}></div>
              </div>
              <div className="profile-info-new">
                <div className="profile-name-new">{user?.name || 'Driver'}</div>
                <div className="profile-email-new">{user?.email || ''}</div>
              </div>
            </div>
          )}

          {/* Navigation Menu */}
          <nav className="sidebar-nav-new">
            <div className="nav-group-new">
              <div className="nav-group-label-new">Main</div>
              <div className="nav-items-new">
                <button 
                  className={`nav-item-new ${activeTab === 'overview' ? 'active' : ''}`}
                  onClick={() => { setActiveTab('overview'); setMobileMenuOpen(false); }}
                >
                  <div className="nav-item-icon-new">
                    <i data-feather="home"></i>
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <span className="nav-item-text-new">Overview</span>
                      {activeTab === 'overview' && <div className="nav-item-indicator-new"></div>}
                    </>
                  )}
                </button>


                <button 
                  className={`nav-item-new ${activeTab === 'edit-profile' ? 'active' : ''}`}
                  onClick={() => { 
                    setActiveTab('edit-profile');
                    setMobileMenuOpen(false);
                    // Fetch latest profile data when switching to edit profile tab
                    if (!profileData && !isLoadingProfile) {
                      fetchDriverProfile();
                    }
                    // Initialize form data when switching to edit profile tab
                    const currentData = profileData || user;
                    if (currentData) {
                      setEditFormData({
                        name: currentData?.name || '',
                        phone: currentData?.phone || '',
                        email: currentData?.email || '',
                        licenseNumber: currentData?.driverDetails?.licenseNumber || currentData?.licenseNumber || '',
                        licensePicture: currentData?.driverDetails?.licensePicture || currentData?.licensePicture || '',
                        vehicleNumber: currentData?.driverDetails?.vehicleNumber || currentData?.vehicleNumber || '',
                        carModel: currentData?.driverDetails?.carModel || currentData?.carModel || '',
                        ambulanceType: currentData?.driverDetails?.ambulanceType || currentData?.ambulanceType || '',
                        carChassisNumber: currentData?.driverDetails?.carChassisNumber || currentData?.carChassisNumber || '',
                        carFitnessNumber: currentData?.driverDetails?.carFitnessNumber || currentData?.carFitnessNumber || '',
                        lastFitnessRenewalDate: currentData?.driverDetails?.lastFitnessRenewalDate 
                          ? new Date(currentData.driverDetails.lastFitnessRenewalDate).toISOString().split('T')[0]
                          : currentData?.lastFitnessRenewalDate || '',
                        lastTaxRenewalDate: currentData?.driverDetails?.lastTaxRenewalDate 
                          ? new Date(currentData.driverDetails.lastTaxRenewalDate).toISOString().split('T')[0]
                          : currentData?.lastTaxRenewalDate || '',
                        providingOrganization: currentData?.driverDetails?.providingOrganization || currentData?.providingOrganization || ''
                      });
                    }
                  }}
                >
                  <div className="nav-item-icon-new">
                    <i data-feather="edit-2"></i>
                  </div>
                  {!sidebarCollapsed && (
                    <>
                      <span className="nav-item-text-new">Edit Profile</span>
                      {activeTab === 'edit-profile' && <div className="nav-item-indicator-new"></div>}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Bottom Actions */}
            {!sidebarCollapsed && (
              <div className="sidebar-footer-new">
                <button 
                  className="footer-action-new"
                  onClick={toggleDarkMode}
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  <i data-feather={isDarkMode ? "sun" : "moon"}></i>
                  <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                </button>
                <button 
                  className="footer-action-new"
                  onClick={handleSignOut}
                >
                  <i data-feather="log-out"></i>
                  <span>Logout</span>
                </button>
              </div>
            )}
            {/* Dark Mode Toggle for Collapsed Sidebar */}
            {sidebarCollapsed && (
              <div className="sidebar-footer-new">
                <button 
                  className="footer-action-new"
                  onClick={toggleDarkMode}
                  title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                >
                  <i data-feather={isDarkMode ? "sun" : "moon"}></i>
                </button>
              </div>
            )}
          </nav>
        </aside>

        {/* Main Content Area */}
        <main className="dashboard-main">
          <div className="content-area">
            {/* Top Header Bar */}
            <div className="dashboard-header-modern">
              <Typography variant="h4" sx={{ fontWeight: 700, color: 'var(--text-primary)' }}>
                Driver Dashboard
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant={isOnline ? "contained" : "outlined"}
                  color={isOnline ? "success" : "inherit"}
                  onClick={toggleOnlineStatus}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: isOnline ? 'white' : 'text.secondary',
                    }}
                  />
                  {isOnline ? 'Online' : 'Offline'}
                </Button>
              </Box>
            </div>

            {/* Overview Tab Content */}
            {activeTab === 'overview' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Active Request Section */}
                {currentRequest && (
                  <>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            bgcolor: 'error.main',
                            color: 'white',
                            p: 1.5,
                            borderRadius: '50%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <i data-feather="truck" style={{ width: 24, height: 24 }}></i>
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          Active Emergency
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Chip
                          label={STATUS_LABELS[currentRequest.status] || currentRequest.status}
                          color={
                            currentRequest.status === 'completed' ? 'success' :
                            currentRequest.status === 'pending' ? 'warning' :
                            'primary'
                          }
                          size="small"
                          sx={{ fontWeight: 600, textTransform: 'uppercase' }}
                        />
                        <Button
                          size="small"
                          onClick={() => setShowStatusGuide(!showStatusGuide)}
                          sx={{ minWidth: 'auto', p: 1 }}
                        >
                          <i data-feather="info" style={{ width: 20, height: 20 }}></i>
                        </Button>
                      </Box>
                    </Box>

                    {showStatusGuide && (
                      <Box
                        sx={{
                          bgcolor: 'primary.light',
                          bgcolor: 'rgba(37, 99, 235, 0.1)',
                          border: '1px solid',
                          borderColor: 'primary.main',
                          borderRadius: 2,
                          p: 2,
                          mb: 3
                        }}
                      >
                        <Typography variant="body2" sx={{ color: 'primary.dark' }}>
                          {STATUS_DESCRIPTIONS[currentRequest.status]}
                        </Typography>
                      </Box>
                    )}

                    {/* Progress Section */}
                    <Box sx={{ mb: 4 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Progress
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {Math.round(getStatusProgress(currentRequest.status))}% Complete
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={getStatusProgress(currentRequest.status)}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            background: 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                            borderRadius: 4
                          }
                        }}
                      />
                      
                      {/* Progress Steps */}
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' },
                          gap: 1.5,
                          mt: 3
                        }}
                      >
                        {STATUS_FLOW.map((step, index) => {
                          const isCompleted = STATUS_FLOW.findIndex(s => s.status === currentRequest.status) >= index;
                          return (
                            <Box
                              key={step.status}
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                p: 1.5,
                                borderRadius: 2,
                                bgcolor: isCompleted ? 'primary.light' : 'grey.100',
                                bgcolor: isCompleted ? 'rgba(37, 99, 235, 0.1)' : 'grey.100',
                                border: '1px solid',
                                borderColor: isCompleted ? 'primary.main' : 'grey.300',
                                textAlign: 'center'
                              }}
                            >
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  bgcolor: isCompleted ? 'primary.main' : 'grey.400',
                                  mb: 1
                                }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: isCompleted ? 600 : 400,
                                  color: isCompleted ? 'primary.dark' : 'text.secondary',
                                  fontSize: '0.7rem'
                                }}
                              >
                                {step.label}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>
                      
                    {/* Request Information */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <i data-feather="user" style={{ width: 20, height: 20, color: '#64748B' }}></i>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                            Patient Information
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {currentRequest.patientInfo?.name || 'Anonymous'}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {currentRequest.patientInfo?.phone || 'N/A'}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <i data-feather="map-pin" style={{ width: 20, height: 20, color: '#64748B' }}></i>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                            Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {typeof currentRequest.location === 'string' 
                              ? currentRequest.location 
                              : currentRequest.location?.address || `${currentRequest.location?.latitude?.toFixed(6)}, ${currentRequest.location?.longitude?.toFixed(6)}` || 'Location not available'}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <i data-feather="truck" style={{ width: 20, height: 20, color: '#64748B' }}></i>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                            Emergency Type
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                            {currentRequest.emergencyType}
                          </Typography>
                        </Box>
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <i data-feather="phone" style={{ width: 20, height: 20, color: '#64748B' }}></i>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                            Description
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>
                            {currentRequest.description}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Update Status
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        {currentRequest.status === REQUEST_STATUS.ACCEPTED && (
                          <Button
                            variant="contained"
                            onClick={() => updateRequestStatus(REQUEST_STATUS.STARTED_JOURNEY)}
                            disabled={isLoading}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            Start Journey
                          </Button>
                        )}
                        {currentRequest.status !== REQUEST_STATUS.ACCEPTED && 
                         currentRequest.status !== REQUEST_STATUS.COMPLETED && 
                         currentRequest.status !== REQUEST_STATUS.CANCELLED && (
                          <Button
                            variant="contained"
                            onClick={() => {
                              const nextStatus = getNextStatus(currentRequest.status);
                              if (nextStatus) {
                                updateRequestStatus(nextStatus.status);
                              }
                            }}
                            disabled={isLoading}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            {getNextStatus(currentRequest.status)?.label || 'Start Journey'}
                          </Button>
                        )}
                        
                        {currentRequest.status === 'IN_PROGRESS' && (
                          <Button
                            variant="contained"
                            color="success"
                            onClick={handleRideCompletion}
                            disabled={isLoading}
                            sx={{ textTransform: 'none', fontWeight: 600 }}
                          >
                            Complete Ride
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Paper>

                  {/* Real-Time Map Tracking */}
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                      Live Tracking
                    </Typography>
                    <DriverTrackingMap
                      requestId={currentRequest.id}
                      patientLocation={(() => {
                        // #region agent log
                        // Priority: initialRequestedLocation > patientLocation > location
                        const rawLocation = currentRequest.initialRequestedLocation || 
                                          currentRequest.patientLocation || 
                                          currentRequest.location;
                        fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:1626',message:'Normalizing patientLocation for DriverTrackingMap',data:{hasCurrentRequest:!!currentRequest,hasInitialLocation:!!currentRequest?.initialRequestedLocation,hasPatientLocation:!!currentRequest?.patientLocation,hasLocation:!!currentRequest?.location,locationType:typeof rawLocation,rawLocationKeys:rawLocation && typeof rawLocation === 'object' ? Object.keys(rawLocation) : [],rawLocationValue:rawLocation},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'I'})}).catch(()=>{});
                        // #endregion agent log
                        
                        // Normalize location format for map component
                        if (!rawLocation) {
                          console.log('No patient location available');
                          return null;
                        }
                        
                        // If it's already in the correct format (has latitude/longitude or lat/lng)
                        if (typeof rawLocation === 'object') {
                          const lat = rawLocation.latitude || rawLocation.lat;
                          const lng = rawLocation.longitude || rawLocation.lng;
                          
                          if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
                            console.log('✅ Valid patient location coordinates found:', { lat, lng });
                            return {
                              latitude: lat,
                              longitude: lng,
                              accuracy: rawLocation.accuracy,
                              address: rawLocation.address
                            };
                          } else {
                            console.warn('⚠️ Patient location object exists but has invalid coordinates:', rawLocation);
                          }
                        } else {
                          console.warn('⚠️ Patient location is not an object (string or other):', typeof rawLocation);
                        }
                        
                        // If it's a string (address), return null - map can't use it
                        // Location updates via WebSocket will provide coordinates
                        return null;
                      })()}
                      onLocationUpdate={(location) => {
                        console.log('Driver location updated:', location);
                        setDriverLocation(location);
                      }}
                      height="500px"
                      className="w-full"
                    />
                  </Paper>
                  </>
                )}

                {/* Available Requests Section */}
                {activeTab === 'overview' && (() => {
                  // #region agent log
                  fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:1751',message:'Rendering Available Requests Section',data:{activeTab,requestsLength:requests.length,isOnline,hasCurrentRequest:!!currentRequest,requestsData:requests.map(r=>({id:r.id,status:r.status,patientName:r.patientInfo?.name}))},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
                  // #endregion agent log
                  return (
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                      <Box
                        sx={{
                          bgcolor: 'warning.main',
                          color: 'white',
                          p: 1.5,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <i data-feather="clock" style={{ width: 24, height: 24 }}></i>
                      </Box>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Emergency Requests
                      </Typography>
                    </Box>
                    
                    {requests.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
                          No pending requests
                        </Typography>
                        {!isOnline && (
                          <Typography variant="body2" sx={{ color: 'error.main' }}>
                            You are currently offline. Toggle your status to receive requests.
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {requests.map((request) => (
                          <Paper key={request.id} elevation={2} sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                              <Chip label={request.status} color="warning" size="small" />
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {new Date(request.timestamp).toLocaleTimeString()}
                              </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <i data-feather="user" style={{ width: 18, height: 18, color: '#64748B' }}></i>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                                    Patient
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {request.patientInfo?.name || 'Anonymous'}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <i data-feather="map-pin" style={{ width: 18, height: 18, color: '#64748B' }}></i>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                                    Location
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {(() => {
                                      // #region agent log
                                      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:1889',message:'Rendering request location',data:{hasLocation:!!request.location,locationType:typeof request.location,hasAddress:!!request.location?.address,hasLatitude:!!request.location?.latitude,hasLongitude:!!request.location?.longitude,latitude:request.location?.latitude,longitude:request.location?.longitude},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'M'})}).catch(()=>{});
                                      // #endregion agent log
                                      
                                      if (!request.location) {
                                        return 'Location not available';
                                      }
                                      
                                      // If it's a string, return it directly
                                      if (typeof request.location === 'string') {
                                        return request.location;
                                      }
                                      
                                      // If it has an address, return it
                                      if (request.location.address) {
                                        return request.location.address;
                                      }
                                      
                                      // Try to get coordinates
                                      const lat = request.location.latitude || request.location.lat;
                                      const lng = request.location.longitude || request.location.lng;
                                      
                                      if (lat != null && lng != null && !isNaN(lat) && !isNaN(lng)) {
                                        return `${typeof lat === 'number' ? lat.toFixed(6) : lat}, ${typeof lng === 'number' ? lng.toFixed(6) : lng}`;
                                      }
                                      
                                      return 'Location not available';
                                    })()}
                                  </Typography>
                                </Box>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <i data-feather="truck" style={{ width: 18, height: 18, color: '#64748B' }}></i>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'text.secondary' }}>
                                    Emergency Type
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                    {request.emergencyType}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', gap: 2 }}>
                              <Button
                                variant="contained"
                                color="success"
                                onClick={() => handleAcceptRequest(request)}
                                disabled={isLoading}
                                fullWidth
                                startIcon={<i data-feather="check" style={{ width: 18, height: 18 }}></i>}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Accept
                              </Button>
                              <Button
                                variant="contained"
                                color="error"
                                onClick={() => handleRejectRequest(request)}
                                disabled={isLoading}
                                fullWidth
                                startIcon={<i data-feather="x" style={{ width: 18, height: 18 }}></i>}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                              >
                                Reject
                              </Button>
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                  );
                })()}

                {/* Profile Tab - Removed */}
                {false && activeTab === 'profile' && (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* #region agent log */}
                    {(() => {
                      fetch('http://127.0.0.1:7243/ingest/c774ac2f-3ff0-4054-a0b4-90f182dc7495',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'DriverDashboard.js:profile-render-start',message:'Profile tab rendering',data:{activeTab,isLoadingProfile,hasProfileData:!!profileData,hasUser:!!user,profileDataType:typeof profileData,userKeys:user ? Object.keys(user) : []},timestamp:Date.now(),sessionId:'debug-session',runId:'profile-debug',hypothesisId:'PROF5'})}).catch(()=>{});
                      return null;
                    })()}
                    {/* #endregion */}
                    {isLoadingProfile ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ ml: 2, color: isDarkMode ? '#94A3B8' : '#64748B' }}>
                          Loading profile...
                        </Typography>
                      </Box>
                    ) : (
                      <>
                        {/* Profile Header Card */}
                    <Paper 
                      elevation={0}
                            sx={{
                            p: 4,
                        borderRadius: 3,
                        background: isDarkMode
                              ? 'linear-gradient(135deg, rgba(30, 64, 175, 0.3) 0%, rgba(37, 99, 235, 0.2) 100%)'
                              : 'linear-gradient(135deg, rgba(30, 64, 175, 0.15) 0%, rgba(37, 99, 235, 0.1) 100%)',
                        border: isDarkMode 
                              ? '1px solid rgba(59, 130, 246, 0.3)' 
                              : '1px solid rgba(30, 64, 175, 0.2)',
                              position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
                            <IconButton
                              onClick={() => setActiveTab('edit-profile')}
                              sx={{
                                bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.1)',
                                color: isDarkMode ? '#60A5FA' : '#1E40AF',
                                '&:hover': {
                                  bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.3)' : 'rgba(30, 64, 175, 0.2)'
                                }
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Box>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'center', md: 'flex-start' }, gap: 3 }}>
                          <Avatar
                              src={user?.profilePicture || profileData?.profilePicture}
                            sx={{
                                width: { xs: 100, md: 140 },
                                height: { xs: 100, md: 140 },
                              border: isDarkMode 
                                  ? '4px solid rgba(59, 130, 246, 0.4)' 
                                  : '4px solid rgba(30, 64, 175, 0.3)',
                              bgcolor: isDarkMode ? 'rgba(59, 130, 246, 0.2)' : 'rgba(30, 64, 175, 0.1)',
                                fontSize: { xs: 50, md: 70 }
                            }}
                          >
                              {!user?.profilePicture && !profileData?.profilePicture && (
                                <PersonIcon sx={{ fontSize: { xs: 50, md: 70 }, color: isDarkMode ? '#60A5FA' : '#1E40AF' }} />
                            )}
                          </Avatar>

                          <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 800,
                                mb: 1,
                                background: isDarkMode
                                  ? 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)'
                                  : 'linear-gradient(135deg, #1E40AF 0%, #2563EB 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                  fontSize: { xs: '1.75rem', md: '2.25rem' }
                                }}
                              >
                                {user?.name || profileData?.name || 'Driver Name'}
                              </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: isDarkMode ? '#BFDBFE' : '#64748B',
                                  mb: 3,
                                  fontSize: { xs: '0.9rem', md: '1rem' }
                              }}
                            >
                                {user?.email || profileData?.email || 'driver@example.com'}
                              </Typography>
                            
                              {/* Quick Stats */}
                              <Grid container spacing={2}>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <LocalAtmIcon sx={{ fontSize: 32, color: isDarkMode ? '#60A5FA' : '#1E40AF', mb: 0.5 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#60A5FA' : '#1E40AF' }}>
                                    {formatCurrency(totalEarnings)}
                              </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', textTransform: 'uppercase', fontWeight: 600 }}>
                                      Earnings
                              </Typography>
                            </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <DirectionsCarIcon sx={{ fontSize: 32, color: isDarkMode ? '#60A5FA' : '#1E40AF', mb: 0.5 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#60A5FA' : '#1E40AF' }}>
                                    {rideHistory.filter(r => r.status === 'completed').length}
                                  </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', textTransform: 'uppercase', fontWeight: 600 }}>
                                      Rides
                                  </Typography>
                          </Box>
                              </Grid>
                              <Grid item xs={4}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <PersonIcon sx={{ fontSize: 32, color: isDarkMode ? '#60A5FA' : '#1E40AF', mb: 0.5 }} />
                                    <Typography variant="h6" sx={{ fontWeight: 700, color: isDarkMode ? '#60A5FA' : '#1E40AF' }}>
                                    {user?.rating?.toFixed(1) || '0.0'}
                              </Typography>
                                    <Typography variant="caption" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', textTransform: 'uppercase', fontWeight: 600 }}>
                                    Rating
                              </Typography>
                            </Box>
                              </Grid>
                            </Grid>
                          </Box>
                      </Box>
                    </Paper>

                        {/* Driver Information Cards */}
                      <Grid container spacing={3}>
                          {/* Personal Information */}
                        <Grid item xs={12} md={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 2,
                                bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)',
                              border: isDarkMode 
                                  ? '1px solid rgba(59, 130, 246, 0.3)' 
                                  : '1px solid rgba(30, 64, 175, 0.15)',
                              height: '100%'
                            }}
                          >
                              <Typography variant="h6" sx={{ mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B', fontWeight: 700, pb: 1, borderBottom: isDarkMode ? '2px solid rgba(59, 130, 246, 0.3)' : '2px solid rgba(30, 64, 175, 0.2)' }}>
                              Personal Information
                              </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <DetailRow label="Full Name" value={user?.name || profileData?.name || 'Not Set'} isDarkMode={isDarkMode} />
                                <DetailRow label="Email" value={user?.email || profileData?.email || 'Not Set'} isDarkMode={isDarkMode} />
                                <DetailRow label="Phone" value={user?.phone || profileData?.phone || 'Not Set'} isDarkMode={isDarkMode} />
                            </Box>
                          </Paper>
                        </Grid>

                          {/* Driver License */}
                        <Grid item xs={12} md={6}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 3,
                              borderRadius: 2,
                                bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)',
                              border: isDarkMode 
                                  ? '1px solid rgba(59, 130, 246, 0.3)' 
                                  : '1px solid rgba(30, 64, 175, 0.15)',
                              height: '100%'
                            }}
                          >
                              <Typography variant="h6" sx={{ mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B', fontWeight: 700, pb: 1, borderBottom: isDarkMode ? '2px solid rgba(59, 130, 246, 0.3)' : '2px solid rgba(30, 64, 175, 0.2)' }}>
                              Driver License
                              </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <DetailRow label="License Number" value={user?.driverDetails?.licenseNumber || profileData?.licenseNumber || 'Not Set'} isDarkMode={isDarkMode} />
                                {(user?.driverDetails?.licensePicture || profileData?.licensePicture) && (
                                  <Box>
                                    <Typography variant="body2" sx={{ color: isDarkMode ? '#D1D5DB' : '#6B7280', fontWeight: 600, mb: 0.5 }}>
                                      License Picture:
                            </Typography>
                                    <a 
                                      href={user?.driverDetails?.licensePicture || profileData?.licensePicture} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{ 
                                        color: isDarkMode ? '#60A5FA' : '#1E40AF',
                                        textDecoration: 'underline',
                                        display: 'inline-block',
                                        marginTop: '4px'
                                      }}
                                    >
                                      View License Picture
                                    </a>
                            </Box>
                                )}
                          </Box>
                        </Paper>
                            </Grid>

                          {/* Vehicle Information */}
                          <Grid item xs={12}>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 3,
                            borderRadius: 2,
                                bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)',
                            border: isDarkMode 
                                  ? '1px solid rgba(59, 130, 246, 0.3)' 
                                  : '1px solid rgba(30, 64, 175, 0.15)'
                              }}
                            >
                              <Typography variant="h6" sx={{ mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B', fontWeight: 700, pb: 1, borderBottom: isDarkMode ? '2px solid rgba(59, 130, 246, 0.3)' : '2px solid rgba(30, 64, 175, 0.2)' }}>
                                Vehicle Information
                          </Typography>
                          <Grid container spacing={2}>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Car Number" value={user?.driverDetails?.vehicleNumber || profileData?.carRegistrationNumber || profileData?.vehicleNumber || 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Car Model" value={user?.driverDetails?.carModel || profileData?.carModel || profileData?.vehicleModel || 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Ambulance Type" value={user?.driverDetails?.ambulanceType || profileData?.ambulanceType || 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Chassis Number" value={user?.driverDetails?.carChassisNumber || profileData?.carChassisNumber || profileData?.chassisNumber || 'Not Set'} isDarkMode={isDarkMode} />
                          </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Fitness Number" value={user?.driverDetails?.carFitnessNumber || profileData?.carFitnessNumber || 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Last Fitness Renewal" value={profileData?.lastFitnessRenewalDate ? new Date(profileData.lastFitnessRenewalDate).toLocaleDateString() : 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                  <DetailRow label="Last Tax Renewal" value={profileData?.lastTaxRenewalDate ? new Date(profileData.lastTaxRenewalDate).toLocaleDateString() : 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                {profileData?.providingOrganization && (
                                  <Grid item xs={12} sm={6} md={4}>
                                    <DetailRow label="Providing Organization" value={profileData?.providingOrganization || user?.driverDetails?.providingOrganization || 'Not Set'} isDarkMode={isDarkMode} />
                            </Grid>
                                )}
                          </Grid>
                        </Paper>
                            </Grid>
                            </Grid>
                      </>
                    )}
                  </Box>
                )}


                {/* History Tab - Removed */}
                {false && activeTab === 'history' && (
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Ride History
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={refreshRideHistory}
                        disabled={isLoadingHistory}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        {isLoadingHistory ? 'Refreshing...' : 'Refresh'}
                      </Button>
                    </Box>

                    {/* Earnings Summary */}
                    <Box
                      sx={{
                        bgcolor: 'success.light',
                        border: 1,
                        borderColor: 'success.main',
                        borderRadius: 2,
                        p: 3,
                        mb: 3
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: 'success.dark' }}>
                            Total Earnings
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
                            {formatCurrency(totalEarnings)}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            Total Rides
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.dark' }}>
                            {rideHistory.length}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'success.main', mt: 0.5, display: 'block' }}>
                            ({rideHistory.filter(r => r.status === 'completed').length} completed)
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Earnings Breakdown by Ambulance Type */}
                    {rideHistory.filter(r => r.status === 'completed').length > 0 && (
                      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                          Earnings by Ambulance Type
                        </Typography>
                        <Grid container spacing={2}>
                          {['Basic', 'Premium', 'ICU', 'NICU'].map((type) => {
                            const typeRides = rideHistory.filter(r => r.status === 'completed' && r.ambulanceType === type);
                            const typeEarnings = typeRides.reduce((sum, ride) => sum + (ride.fare || 0), 0);
                            if (typeRides.length === 0) return null;
                            
                            return (
                              <Grid item xs={12} sm={6} md={3} key={type}>
                                <Box sx={{ 
                                  p: 2, 
                                  borderRadius: 2, 
                                  bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.8)' : 'white',
                                  border: isDarkMode ? '1px solid rgba(59, 130, 246, 0.3)' : '1px solid rgba(30, 64, 175, 0.15)'
                                }}>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 600 }}>
                                    {type}
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main', mt: 0.5 }}>
                                    {formatCurrency(typeEarnings)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                    {typeRides.length} {typeRides.length === 1 ? 'ride' : 'rides'}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block', fontSize: '0.7rem' }}>
                                    {type === 'Basic' && 'Base: 500 BDT'}
                                    {type === 'Premium' && 'Base: 2000 BDT'}
                                    {type === 'ICU' && 'Base: 5000 BDT'}
                                    {type === 'NICU' && 'Base: 6000 BDT'}
                                  </Typography>
                                </Box>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Paper>
                    )}

                    {/* Ride History List */}
                    {isLoadingHistory ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                        <CircularProgress />
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 2 }}>
                          Loading ride history...
                        </Typography>
                      </Box>
                    ) : rideHistory.length === 0 ? (
                      <Box sx={{ textAlign: 'center', py: 8 }}>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                          No rides yet
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                          Your accepted rides will appear here
                        </Typography>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {rideHistory.map((ride) => (
                          <Paper
                            key={ride.id}
                            elevation={2}
                            sx={{
                              p: 2,
                              borderRadius: 2,
                              border: 1,
                              borderColor: 'divider',
                              '&:hover': {
                                boxShadow: 4
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                              <Box sx={{ flex: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {ride.patientName}
                                  </Typography>
                                  <Chip
                                    label={ride.ambulanceType}
                                    size="small"
                                    sx={{
                                      textTransform: 'capitalize',
                                      bgcolor: ride.ambulanceType === 'vip' ? 'warning.light' :
                                                ride.ambulanceType === 'critical' ? 'error.light' : 'primary.light',
                                      color: ride.ambulanceType === 'vip' ? 'warning.dark' :
                                              ride.ambulanceType === 'critical' ? 'error.dark' : 'primary.dark'
                                    }}
                                  />
                                  <Chip
                                    label={ride.status === 'completed' ? 'Completed' : 
                                           ride.status === 'accepted' ? 'Accepted' :
                                           ride.status === 'in-progress' ? 'In Progress' :
                                           ride.status || 'Active'}
                                    size="small"
                                    color={ride.status === 'completed' ? 'success' : 
                                           ride.status === 'accepted' ? 'info' : 'warning'}
                                    sx={{ textTransform: 'capitalize' }}
                                  />
                                </Box>
                                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Pickup
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {ride.pickupLocation}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      Dropoff
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {ride.dropoffLocation}
                                    </Typography>
                                  </Box>
                                </Box>
                              </Box>
                              <Box sx={{ textAlign: 'right', ml: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                                  {formatCurrency(ride.fare)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                  {ride.ambulanceType || 'Basic'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5, display: 'block' }}>
                                  {formatDate(ride.date)}
                                </Typography>
                              </Box>
                            </Box>

                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                pt: 2,
                                borderTop: 1,
                                borderColor: 'divider'
                              }}
                            >
                              <Box sx={{ display: 'flex', gap: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <i data-feather="clock" style={{ width: 16, height: 16, color: '#64748B' }}></i>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {ride.duration}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <i data-feather="map-pin" style={{ width: 16, height: 16, color: '#64748B' }}></i>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {ride.distance}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <i data-feather="clock" style={{ width: 16, height: 16, color: '#64748B' }}></i>
                                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                    {ride.time}
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip
                                label={ride.status}
                                size="small"
                                color="success"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    )}
                  </Paper>
                )}
              </Box>
            )}
          </div>
        </main>
      </div>

      {/* Ride Completion Modal */}
      <Dialog
        open={showRideCompletion && !!completedRide}
        onClose={closeRideCompletion}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: 'success.light',
              mb: 2
            }}
          >
            <i data-feather="check" style={{ width: 24, height: 24, color: '#10B981' }}></i>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Ride Completed!
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
            Thank you for your service
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Patient:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{completedRide?.patientName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Duration:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{completedRide?.duration}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Distance:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{completedRide?.distance}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Fare:</Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                ৳{completedRide?.fare} BDT
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={closeRideCompletion}
            variant="outlined"
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
          <Button
            onClick={() => {
              closeRideCompletion();
              setActiveTab('history');
            }}
            variant="contained"
            color="success"
            fullWidth
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View History
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Profile Modal - Shows All Driver Database Details */}
      <Dialog
        open={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            maxHeight: '90vh',
            overflow: 'auto',
            zIndex: 9999
          }
        }}
        sx={{
          zIndex: 9999,
          '& .MuiBackdrop-root': {
            zIndex: 9998
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Typography component="span" variant="h5" sx={{ fontWeight: 700, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
            Driver Profile Details
          </Typography>
          <IconButton
            onClick={() => setShowProfileModal(false)}
            sx={{ color: isDarkMode ? '#94A3B8' : '#64748B' }}
          >
            <i data-feather="x"></i>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {isLoadingProfile ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, color: 'text.secondary' }}>
                Loading profile data...
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Personal Information Section */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                  Personal Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Name" value={profileData?.name || user?.name || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Email" value={profileData?.email || user?.email || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Phone" value={profileData?.phone || profileData?.mobileNumber || user?.phone || user?.driverDetails?.phone || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Role" value={user?.role || 'Driver'} isDarkMode={isDarkMode} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Driver License Information */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                  Driver License Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DetailRow 
                      label="License Number" 
                      value={profileData?.licenseNumber || user?.driverDetails?.licenseNumber || 'N/A'} 
                      isDarkMode={isDarkMode} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: isDarkMode ? 'rgba(15, 23, 42, 0.3)' : 'rgba(255, 255, 255, 0.5)' }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, textTransform: 'uppercase', color: isDarkMode ? '#94A3B8' : '#64748B', fontSize: '0.7rem' }}>
                        License Picture
                      </Typography>
                      {profileData?.licensePicture || user?.driverDetails?.licensePicture ? (
                        <Box sx={{ mt: 1 }}>
                          <img 
                            src={profileData?.licensePicture || user?.driverDetails?.licensePicture} 
                            alt="License" 
                            style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px' }}
                          />
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: isDarkMode ? '#94A3B8' : '#64748B', mt: 0.5 }}>
                          No license picture uploaded
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Vehicle Information */}
              <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)' }}>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                  Vehicle Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Car Number" value={profileData?.carRegistrationNumber || profileData?.vehicleNumber || user?.driverDetails?.vehicleNumber || user?.driverDetails?.carRegistrationNumber || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Car Model" value={profileData?.carModel || user?.driverDetails?.carModel || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Ambulance Type" value={profileData?.ambulanceType || user?.driverDetails?.ambulanceType || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Chassis Number" value={profileData?.carChassisNumber || user?.driverDetails?.carChassisNumber || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Fitness Number" value={profileData?.carFitnessNumber || user?.driverDetails?.carFitnessNumber || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow 
                      label="Last Fitness Renewal" 
                      value={profileData?.lastFitnessRenewalDate || user?.driverDetails?.lastFitnessRenewalDate 
                        ? new Date(profileData?.lastFitnessRenewalDate || user?.driverDetails?.lastFitnessRenewalDate).toLocaleDateString()
                        : 'N/A'} 
                      isDarkMode={isDarkMode} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow 
                      label="Last Tax Renewal" 
                      value={profileData?.lastTaxRenewalDate || user?.driverDetails?.lastTaxRenewalDate 
                        ? new Date(profileData?.lastTaxRenewalDate || user?.driverDetails?.lastTaxRenewalDate).toLocaleDateString()
                        : 'N/A'} 
                      isDarkMode={isDarkMode} 
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <DetailRow label="Providing Organization" value={profileData?.providingOrganization || user?.driverDetails?.providingOrganization || 'N/A'} isDarkMode={isDarkMode} />
                  </Grid>
                </Grid>
              </Paper>

              {/* Additional Information */}
              {(profileData || user) && (
                <Paper elevation={0} sx={{ p: 3, borderRadius: 2, bgcolor: isDarkMode ? 'rgba(30, 41, 59, 0.6)' : 'rgba(240, 249, 255, 0.8)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: isDarkMode ? '#E0F2FE' : '#1E293B' }}>
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    {user?._id && (
                      <Grid item xs={12} sm={6}>
                        <DetailRow label="User ID" value={user._id} isDarkMode={isDarkMode} />
                      </Grid>
                    )}
                    {user?.createdAt && (
                      <Grid item xs={12} sm={6}>
                        <DetailRow label="Account Created" value={new Date(user.createdAt).toLocaleDateString()} isDarkMode={isDarkMode} />
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2, borderTop: 1, borderColor: 'divider' }}>
          <Button
            onClick={() => {
              // Close the profile modal first
              setShowProfileModal(false);
              // Use longer timeout to ensure modal fully closes and backdrop is removed
              setTimeout(() => {
                setActiveTab('edit-profile');
              }, 300);
            }}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Edit Profile
          </Button>
          <Button
            onClick={() => setShowProfileModal(false)}
            variant="outlined"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
    </ThemeProvider>
  );
};

export default EmergencyDriverDashboard; 